// ── Pause the bottom nav's frosted blur while something animates ─────────────
// On the gradient backgrounds the mobile nav is frosted glass (backdrop-filter,
// .soft-nav in App.jsx). Many Android phones redraw the whole screen on every
// frame of any animation, so that blur is recomputed every frame too: the
// Clock-In Done pop and calendar swipes lagged on gradients only, smooth on
// Default, which has no blur (Jade, 2026-10-21). Bottom sheets already turn it
// off with the "no-nav-blur" class; this does the same for short animations.
//
// The class goes on the nav element itself (not <html>), so toggling it
// restyles one element, not the whole app.
//
// While paused the bar shows the page's own background where the bar sits,
// under the same 55% white, instead of the blur (Jade, 2026-10-28: the plain
// 85% white it used to get flashed white on every calendar swipe). A blurred
// smooth gradient looks like the gradient itself, so this reads as the same
// frosted bar, and it's a still picture: nothing to redo per frame. Measured
// against the real frosted bar (mean pixel difference, Planner Month): Pastel
// 7.4 -> 1.8, Dusk 9.6 -> 0.6, Peach 5.0 -> 1.5, Terra (a blurred mesh) 20.9 -> 8.2.
// If the background can't be read, the CSS fallback (a whiter fill) shows.

let holds = 0;
let restoreTimer = 0;

const OVERLAY = "linear-gradient(rgba(255,255,255,0.55), rgba(255,255,255,0.55))";
const PROPS = ["background-image", "background-color", "background-size", "background-position", "background-repeat"];

// Splits a computed background-image list into its layers (commas inside a
// gradient's brackets don't split).
export function splitLayers(list) {
  if (!list || list === "none") return [];
  const out = [];
  let depth = 0, start = 0;
  for (let i = 0; i < list.length; i++) {
    const c = list[i];
    if (c === "(") depth++;
    else if (c === ")") depth--;
    else if (c === "," && depth === 0) { out.push(list.slice(start, i).trim()); start = i + 1; }
  }
  out.push(list.slice(start).trim());
  return out.filter(Boolean);
}

// The paused bar's background: the 55% white, then the page's own layers
// placed where they are on screen behind the bar. A plain gradient covers the
// page's box from its top, one window-height tall (`wpH`, see App.jsx's
// setWallpaperHeight); a mesh is App.jsx's ::before layer, 140% of the window
// each way from -20%. Sizes are in px so the bar's own position shifts them.
export function pausedNavBackground({ layers, color, mesh, navRect, mainTop = 0, vw, wpH }) {
  const page = splitLayers(layers);
  if (!page.length && (!color || color === "transparent" || /rgba\([^)]*,\s*0\)$/.test(color))) return null;
  const w = mesh ? vw * 1.4 : vw, h = mesh ? wpH * 1.4 : wpH;
  const x = (mesh ? -0.2 * vw : 0) - navRect.left;
  const y = (mesh ? -0.2 * wpH : mainTop) - navRect.top;
  const sizes = ["100% 100%", ...page.map(() => `${w}px ${h}px`)];
  const positions = ["0 0", ...page.map(() => `${x}px ${y}px`)];
  return {
    "background-image": [OVERLAY, ...page].join(", "),
    "background-color": color || "transparent",
    "background-size": sizes.join(", "),
    "background-position": positions.join(", "),
    "background-repeat": ["repeat", ...page.map(() => "no-repeat")].join(", "),
  };
}

// Reads the page background behind `nav` from .clarity-main (or its ::before
// mesh layer).
function backgroundBehind(nav) {
  const main = document.querySelector(".clarity-main");
  if (!main || typeof window === "undefined" || !window.getComputedStyle) return null;
  const before = window.getComputedStyle(main, "::before");
  const mesh = !!before && before.content && before.content !== "none" && before.backgroundImage && before.backgroundImage !== "none";
  const src = mesh ? before : window.getComputedStyle(main);
  const wpH = parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue("--wp-h")) || window.innerHeight;
  return pausedNavBackground({
    layers: src.backgroundImage,
    color: src.backgroundColor,
    mesh,
    navRect: nav.getBoundingClientRect(),
    mainTop: mesh ? 0 : main.getBoundingClientRect().top,
    vw: window.innerWidth,
    wpH,
  });
}

const setPaused = (on) => {
  if (typeof document === "undefined") return;
  document.querySelectorAll(".clarity-sidebar.soft-nav").forEach(el => {
    if (on) {
      let bg = null;
      try { bg = backgroundBehind(el); } catch { bg = null; }
      if (bg) PROPS.forEach(p => el.style.setProperty(p, bg[p], "important"));
    } else {
      PROPS.forEach(p => el.style.removeProperty(p));
    }
    el.classList.toggle("nav-blur-paused", on);
  });
};

// Returns a release function (safe to call more than once).
export function holdNavBlur() {
  holds++;
  clearTimeout(restoreTimer);
  setPaused(true);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holds = Math.max(0, holds - 1);
    if (holds) return;
    // A short grace so the frame that lands the animation isn't the one that
    // turns the blur back on.
    restoreTimer = setTimeout(() => { if (!holds) setPaused(false); }, 200);
  };
}

export function pauseNavBlur(ms) {
  const release = holdNavBlur();
  setTimeout(release, ms);
}
